'use client';

import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GamePanel } from '../ui/GamePanel';
import { LiveDot } from '../ui/LiveDot';
import { ChatPanel as SdkChatPanel } from '@/components/chat';
import type { MockBoardHolder } from '../data/mock-boards';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { TutorialChatBody } from '@/features/tutorial/TutorialChatBody';

interface ChatPanelProps {
  /** Active board id — passed straight through as the SDK chat room.
   *  This may be a reserved label (`'boardroom'`, `'megapot-grid'`) or a
   *  live composite grid id (`0x<ugm>-<tokenId>`); both shapes are
   *  accepted by `isChatRoomId` on the server. When no board is active
   *  we default to `'boardroom'` so the panel never renders with an
   *  empty room. */
  boardId?: string;
  /** @deprecated Reserved for future title variants; panel title is "Chat". */
  boardName?: string;
  /** Top holders for the active board. Reserved for future affordances
   *  (e.g. avatar stacks in the title bar). Currently unused — kept on
   *  the prop API so consumers in `LobbyClient.tsx` don't need to
   *  branch when re-wiring. */
  holders?: MockBoardHolder[];
  online?: number;
  className?: string;
  /**
   * Layout shape:
   *   'floating' (default) — fixed-height card meant to live inside a
   *                          DraggableFloatingPanel. The chat body is
   *                          capped at ~280px so the card stays compact.
   *   'docked'             — full vertical sidebar mounted at the right
   *                          edge of the canvas. The chat body stretches
   *                          to fill all remaining space.
   */
  variant?: 'floating' | 'docked';
  /** Driven by `isBoardDataLoading` in `LobbyClient` — while true we
   *  unmount the SDK chat and render a skeleton message stack, so the
   *  viewer doesn't see the previous board's messages during a switch.
   *  When the flag clears, the SDK remounts with the new `room`. */
  loading?: boolean;
  /** Chat-mention deep-link target. When set, the SDK chat scrolls to +
   *  briefly highlights the matching `chat_messages.id` once history
   *  finishes hydrating. Cleared by the SDK via `onMessageFocusConsumed`
   *  so a second navigation to the same id triggers a fresh scroll
   *  instead of being a no-op. */
  messageFocusId?: number | null;
  /** Called by the SDK chat once a deep-link focus has been honored
   *  (or proven impossible because the message isn't in the loaded
   *  history). Lets the lobby drop the id from local state. */
  onMessageFocusConsumed?: () => void;
}

/**
 * Lobby chat panel — gilded GamePanel chrome wrapping the real SDK
 * `<ChatPanel>` from `@/components/chat`.
 *
 * The SDK chat handles connect/verify/holder-gate states, infinite
 * scroll history, SSE live updates, send/edit/delete/react/reply, and
 * typing indicators — see `takeover-app/src/components/chat/chat-panel.tsx`.
 *
 * Every board gets its own chat room. The active `boardId` is forwarded
 * verbatim as the SDK `room` prop — either a reserved label
 * (`'boardroom'`, `'megapot-grid'`) or a live composite grid id
 * (`0x<ugm>-<tokenId>`). The server validates the shape via
 * `isChatRoomId` and rejects anything else, so no client-side filter
 * is needed here. We only default to `'boardroom'` when `boardId` is
 * missing, so the panel still renders something sensible before a
 * board is selected.
 */
export function ChatPanel({
  boardId,
  online,
  className,
  variant = 'floating',
  loading = false,
  messageFocusId = null,
  onMessageFocusConsumed,
}: ChatPanelProps) {
  const docked = variant === 'docked';
  const room = boardId || 'boardroom';
  // Tutorial mode short-circuits the SDK chat entirely — the tutorial
  // wallet has no real verify token / SSE subscription, so the SDK
  // panel would fall through to the holder-gate banner here. Detecting
  // the tutorial context lets us render a curated static backlog that
  // looks identical (same `ChatMessage` rows, same chrome) so the
  // `holder-chat` spotlight lands on a populated panel.
  const tutorial = useTutorialOptional();
  const isTutorial = tutorial?.isActive ?? false;

  return (
    <GamePanel
      title="Chat"
      draggable={!docked}
      className={cn('w-[336px]', docked && 'h-full min-h-0 flex flex-col overflow-hidden', className)}
      bodyClassName={cn('p-0', docked && 'flex-1 min-h-0 flex flex-col')}
      trailing={
        <span className="flex items-center gap-1.5">
          <Lock className="h-2.5 w-2.5 text-amber-300/80" />
          <LiveDot color={loading ? 'gold' : 'green'} size={5} />
          {typeof online === 'number' && !loading && (
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-emerald-300/80">
              {online}
            </span>
          )}
        </span>
      }
    >
      {loading ? (
        <ChatSkeleton docked={docked} />
      ) : isTutorial ? (
        <TutorialChatBody docked={docked} />
      ) : (
        <SdkChatPanel
          room={room}
          messageFocusId={messageFocusId}
          onMessageFocusConsumed={onMessageFocusConsumed}
          className={cn(
            // SDK panel needs to fill its parent — both for the docked
            // sidebar (which stretches to the canvas height) and the
            // floating variant (which caps height so the card stays compact).
            'flex flex-col bg-transparent text-[12px] text-amber-100/90',
            '[&_.text-muted-foreground]:text-amber-200/55',
            docked ? 'flex-1 min-h-0 overflow-hidden overscroll-y-contain' : 'h-[340px]',
          )}
        />
      )}
    </GamePanel>
  );
}

/** Message-stack placeholder used between board switches. Varies row widths
 *  so the skeleton reads like a real chat backlog rather than one big bar,
 *  and reserves the same min-height/flex shape as the SDK panel so the HUD
 *  doesn't reflow when the SDK remounts. */
function ChatSkeleton({ docked }: { docked: boolean }) {
  const rows = [88, 64, 76, 52, 72, 80, 44, 68];
  return (
    <div
      className={cn(
        'flex flex-col gap-2 px-3 py-3',
        docked ? 'flex-1 min-h-0 overflow-hidden' : 'h-[340px]',
      )}
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading chat"
    >
      {rows.map((w, i) => (
        <div
          key={i}
          className="flex items-start gap-2"
          style={{ opacity: 1 - i * 0.08 }}
        >
          <div className="h-5 w-5 rounded-full bg-amber-500/10 animate-pulse shrink-0" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="h-2 w-12 rounded-[2px] bg-amber-500/10 animate-pulse" />
            <div
              className="h-2.5 rounded-[2px] bg-amber-500/[0.08] animate-pulse"
              style={{ width: `${w}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
